import { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { countMultipolygonVertices } from '~/_shared/types/polygon/polygon.utils';
import {
  isRadiusProximity, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { convertDistanceToUnitSystem } from '~/_shared/utils/distance/distance.helpers';
import { getProximityRadius } from '~/_shared/utils/proximity/getProximityRadius';
import {
  calculateAreaInSquaredMeters, getAreaInUnit,
} from '~/map/map/distanceCalculator/calculateDistance';

export const AGS_REPORT_MAX_RADIUS_IN_MILES = 50;
export const AGS_MAX_POLYGON_VERTICES = 8000;
export const AGS_MAX_AREA_IN_SQUARE_MILES = 100_000;

export const AGS_AREA_VALIDATION_ERROR_CODES = {
  PolygonTooComplex: 'polygonTooComplex',
  PolygonTooLarge: 'polygonTooLarge',
  RadiusTooLarge: 'radiusTooLarge',
} as const;

export const REPORT_VALIDATION_ERROR_CODES = {
  ...AGS_AREA_VALIDATION_ERROR_CODES,
  AreaNotAvailable: 'areaNotAvailable',
  RadiiNotSupportedInArea: 'radiiNotSupportedInArea',
} as const;

export const validateRadiusForAgsApi = (proximity: Proximity, spreadsheetRowId?: SpreadsheetRowId) => {
  if (isRadiusProximity(proximity)) {
    const radialDistance = getProximityRadius(proximity, spreadsheetRowId);

    if (!radialDistance) {
      return false;
    }

    const radius = convertDistanceToUnitSystem({
      distance: radialDistance.radius,
      currentUnitSystem: radialDistance.unit,
      targetUnitSystem: UnitSystem.imperial,
    });

    return radius <= AGS_REPORT_MAX_RADIUS_IN_MILES;
  }

  return true;
};

export const validatePolygonForAgsApi = (polygon: MultiPolygon) => {
  const polygonVertices = countMultipolygonVertices(polygon);

  if (polygonVertices > AGS_MAX_POLYGON_VERTICES) {
    return {
      isValid: false,
      reason: 'exportReport.polygonTooComplex',
      errorCode: AGS_AREA_VALIDATION_ERROR_CODES.PolygonTooComplex,
    } as const;
  }

  const areaInSquareMeters = polygon.reduce((totalArea, polygon) => totalArea + calculateAreaInSquaredMeters(polygon.path), 0);
  const areaInSquareMiles = getAreaInUnit(areaInSquareMeters, UnitSystem.imperial);
  if (areaInSquareMiles > AGS_MAX_AREA_IN_SQUARE_MILES) {
    return {
      isValid: false,
      reason: 'exportReport.polygonTooLarge',
      errorCode: AGS_AREA_VALIDATION_ERROR_CODES.PolygonTooLarge,
    } as const;
  }

  return { isValid: true } as const;
};
